// src/lib/demoRecovery.js — the trailer re-enters by itself when its session dies.
//
// Every demo visitor signs in as the same principal (api/demo-session.js), so a
// GLOBAL sign-out anywhere — a visitor clicking Sign out, a probe script — used
// to revoke every open demo tab at once, and the Home showed "Invalid session"
// with no way back except clearing site data (2026-09-24). Two rules close it:
// sign-outs in the demo are LOCAL (AuthContext), and a tab whose session the
// server no longer honours clears it and goes back through /demo, which mints a
// fresh one. Inert outside demo mode.
//
// Kept apart from demoClient.js on purpose: demoClient is imported by modules
// under vitest, and this file imports the Supabase client (CLAUDE.md: a vitest
// file must never reach src/lib/supabase.js).
import { supabase } from './supabase'
import { IS_DEMO } from './demoClient'

let recovering = false

/** Drop this tab's dead session and re-enter the trailer. No-op outside the demo. */
export async function recoverDemoSession() {
  if (!IS_DEMO || recovering) return
  recovering = true
  try { await supabase.auth.signOut({ scope: 'local' }) } catch { /* already gone */ }
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith('sb-')) localStorage.removeItem(k)
  } catch { /* private mode */ }
  window.location.replace('/demo')
}

/**
 * Ask the auth server whether this tab's session is still good. True when it is,
 * or when the answer is unknowable (offline) — only a definite auth refusal
 * counts as dead, so a network blip never bounces a visitor to the front door.
 */
export async function demoSessionAlive() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return false
  const { data, error } = await supabase.auth.getUser()
  if (data?.user) return true
  const status = error?.status
  return !(status === 401 || status === 403 || /session|jwt|token/i.test(error?.message || ''))
}
